import { useLocation, useNavigate } from "react-router-dom";
import { Badge, ScrollArea, Text } from "@mantine/core";
import {
  IconActivity,
  IconBuildingStore,
  IconCategory,
  IconChartLine,
  IconGitCompare,
  IconLayoutGrid,
  IconRadar,
  IconReportSearch,
  IconRobot,
  IconShoppingCart,
  IconSparkles,
  IconTag,
  IconUsers,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { useMemo } from "react";
import { MOCK_BRANDS } from "../mock/brands";
import classes from "./Navbar.module.css";

interface NavItem {
  link: string;
  label: string;
  icon: ComponentType<{ className?: string; stroke?: number }>;
  badge?: number;
}

const analyticsItems: NavItem[] = [
  { link: "/", label: "Feed", icon: IconActivity },
  { link: "/grid", label: "Pricing Grid", icon: IconLayoutGrid },
  { link: "/history", label: "Price History", icon: IconChartLine },
  { link: "/comparison", label: "Comparison", icon: IconGitCompare },
  { link: "/catalog", label: "Catalog", icon: IconShoppingCart },
];

function isActive(itemLink: string, pathname: string): boolean {
  if (itemLink === "/") return pathname === "/";
  return pathname.startsWith(itemLink);
}

function NavLink({ item, pathname }: { item: NavItem; pathname: string }) {
  const navigate = useNavigate();

  return (
    <a
      className={classes.link}
      data-active={isActive(item.link, pathname) || undefined}
      href={item.link}
      onClick={(event) => {
        event.preventDefault();
        navigate(item.link);
      }}
    >
      <item.icon className={classes.linkIcon} stroke={1.5} />
      <span>{item.label}</span>
      {item.badge != null && (
        <Badge size="xs" variant="filled" color="red" ml="auto" aria-label={`${item.badge} items pending review`}>
          {item.badge}
        </Badge>
      )}
    </a>
  );
}

export function Navbar() {
  const { pathname } = useLocation();

  const pendingCount = useMemo(
    () => MOCK_BRANDS.reduce((sum, b) => sum + b.pendingReviewCount, 0),
    [],
  );

  const adminItems: NavItem[] = useMemo(
    () => [
      { link: "/admin/discover", label: "Discover", icon: IconRadar },
      { link: "/admin/products", label: "Products", icon: IconShoppingCart },
      {
        link: "/admin/brands",
        label: "Brands",
        icon: IconTag,
        badge: pendingCount > 0 ? pendingCount : undefined,
      },
      { link: "/admin/retailers", label: "Retailers", icon: IconBuildingStore },
      { link: "/admin/categories", label: "Categories", icon: IconCategory },
      { link: "/admin/classify", label: "Classify", icon: IconSparkles },
      { link: "/admin/scrapers", label: "Scrapers", icon: IconRobot },
      { link: "/admin/review-queue", label: "Review Queue", icon: IconReportSearch },
      { link: "/admin/users", label: "Users", icon: IconUsers },
    ],
    [pendingCount],
  );

  return (
    <nav className={classes.navbar}>
      <ScrollArea className={classes.navbarMain}>
        <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb={4} px="sm">
          Analytics
        </Text>
        {analyticsItems.map((item) => (
          <NavLink key={item.link} item={item} pathname={pathname} />
        ))}

        <Text size="xs" fw={500} c="dimmed" tt="uppercase" mt="lg" mb={4} px="sm">
          Admin
        </Text>
        {adminItems.map((item) => (
          <NavLink key={item.link} item={item} pathname={pathname} />
        ))}
      </ScrollArea>
    </nav>
  );
}
